<template>
    <div class="producto-detalle-page">
        <!-- Header -->
        <section class="page-header">
            <div class="container">
                <nav class="breadcrumbs">
                    <router-link to="/">Inicio</router-link>
                    <span class="separator">/</span> 
                    <span class="">Productos</span>
                    <span class="separator">/</span>
                    <router-link v-if="producto.categoria" :to="`/productos/${producto.categoria.id}/${producto.categoria.slug}`">
                        {{ producto.categoria.nombre }}
                    </router-link>
                    <span class="separator">/</span>
                    <span class="current">{{ producto.nombre }}</span>
                </nav>
                <h1 class="page-title">{{ producto.nombre }}</h1>
                <p v-if="producto.categoria" class="page-subtitle">
                    {{ producto.categoria.nombre }}
                </p>
            </div>
        </section>

        <!-- Product Content -->
        <section class="product-content">
            <div class="container">
                <div class="product-layout">
                    <!-- Gallery -->
                    <div class="product-gallery">
                        <div class="main-image">
                            <img :src="currentImage" :alt="producto.nombre">
                        </div>
                        <div v-if="allImages.length > 1" class="thumbnails">
                            <div 
                                v-for="(img, index) in allImages" 
                                :key="index"
                                class="thumbnail"
                                :class="{ 'active': currentImage === img }"
                                @click="currentImage = img"
                            >
                                <img :src="img" :alt="`${producto.nombre} ${index + 1}`">
                            </div>
                        </div>
                    </div>

                    <!-- Product Info -->
                    <div class="product-info">
                        <span v-if="producto.marca" class="product-brand">
                            {{ producto.marca.nombre }}
                        </span>
                        
                        <p v-if="producto.sku" class="product-sku">
                            SKU: <span>{{ producto.sku }}</span>
                        </p>

                        <!-- Meta Info -->
                        <div class="product-meta">
                            <div v-if="producto.categoria" class="meta-item">
                                <i class="fas fa-tag"></i>
                                <span>Categoría: {{ producto.categoria.nombre }}</span>
                            </div>
                            <div v-if="producto.modelo" class="meta-item">
                                <i class="fas fa-cube"></i>
                                <span>Modelo: {{ producto.modelo.nombre }}</span>
                            </div>
                        </div>

                        <!-- Description -->
                        <div v-if="producto.descripcion" class="product-description">
                            <h3>Descripción</h3>
                            <div v-html="producto.descripcion"></div>
                        </div>

                        <!-- Características -->
                        <div v-if="producto.caracteristicas" class="product-features">
                            <h3>Características</h3>
                            <div v-html="producto.caracteristicas"></div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="product-actions">
                            <a 
                                v-if="producto.brochure" 
                                :href="`/storage/${producto.brochure}`" 
                                target="_blank"
                                class="btn-action btn-primary"
                            >
                                <i class="fas fa-download"></i>
                                Descargar Ficha Técnica
                            </a>
                            
                            <a 
                                v-if="producto.link_driver" 
                                :href="producto.link_driver" 
                                target="_blank"
                                class="btn-action btn-secondary"
                            >
                                <i class="fas fa-download"></i>
                                Descargar Drivers
                            </a>
                            
                            <router-link 
                                to="/contacto"
                                class="btn-action btn-contact"
                            >
                                <i class="fas fa-envelope"></i>
                                Solicitar Cotización
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Related Products -->
        <section v-if="relacionados.length" class="related-products">
            <div class="container">
                <h2 class="section-title">Productos Relacionados</h2>
                <p class="section-subtitle">Otros productos que podrían interesarte</p>
                
                <div class="products-grid">
                    <ProductCard 
                        v-for="producto in relacionados" 
                        :key="producto.id"
                        :producto="producto"
                    />
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import ProductCard from '../components/products/ProductCard.vue';
import api from '../services/api';

export default {
    name: 'ProductoDetalle',
    components: {
        ProductCard
    },
    data() {
        return {
            producto: {
                nombre: '',
                marca: null,
                modelo: null,
                categoria: null,
                imagenes: []
            },
            relacionados: [],
            currentImage: '',
            loading: true
        };
    },
    computed: {
        allImages() {
            const images = [];
            
            // Imagen principal
            if (this.producto.imagen_principal) {
                images.push(`/storage/${this.producto.imagen_principal}`);
            }
            
            // Galería adicional
            if (this.producto.imagenes && this.producto.imagenes.length) {
                this.producto.imagenes.forEach(img => {
                    images.push(`/storage/${img.imagen}`);
                });
            }
            
            // Si no hay imágenes, usar placeholder
            if (images.length === 0) {
                images.push('/storage/no_image.jpg?text=Producto');
            }
            
            return images;
        }
    },
    async created() {
        await this.loadProduct();
        await this.loadRelated();
    },
    methods: {
        async loadProduct() {
            try {
                this.loading = true;
                const productId = this.$route.params.id;
                const response = await api.getProduct(productId);
                this.producto = response.data;
                this.currentImage = this.allImages[0];
                this.loading = false;
            } catch (error) {
                console.error('Error loading product:', error);
                this.$router.push('/');
            }
        },
        async loadRelated() {
            try {
                const productId = this.$route.params.id;
                const response = await api.getRelatedProducts(productId);
                this.relacionados = response.data || [];
            } catch (error) {
                console.error('Error loading related products:', error);
            }
        }
    },
    watch: {
        '$route'() {
            this.loadProduct();
            this.loadRelated();
        }
    }
};
</script>

<style scoped>
.producto-detalle-page {
    background-color: var(--background);
    min-height: 100vh;
    padding-top: 80px;
}

.page-header {
    background: linear-gradient(135deg, var(--primary-dark), var(--primary-blue));
    padding: 60px 2rem;
    position: relative;
}

.page-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(212, 175, 55, 0.1), transparent 60%);
}

.breadcrumbs {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
}

.breadcrumbs a {
    color: var(--text-gray);
    text-decoration: none;
    transition: color 0.3s ease;
}

.breadcrumbs a:hover {
    color: var(--accent-gold);
}

.breadcrumbs .separator {
    color: rgba(212, 175, 55, 0.3);
}

.breadcrumbs .current {
    color: var(--accent-gold);
    font-weight: 600;
}

.container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
}

.page-title {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--text-light);
}

.page-subtitle {
    font-size: 1.2rem;
    color: var(--text-gray);
}

.product-content {
    padding: 60px 2rem;
}

.product-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
}

/* GALLERY */
.product-gallery {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.main-image {
    width: 100%;
    height: 600px;
    background: var(--card-bg);
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid rgba(212, 175, 55, 0.1);
}

.main-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.thumbnails {
    display: flex;
    gap: 1rem;
    overflow-x: auto;
}

.thumbnail {
    width: 100px;
    height: 100px;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.thumbnail:hover,
.thumbnail.active {
    border-color: var(--accent-gold);
}

.thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* PRODUCT INFO */
.product-info {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.product-brand {
    display: inline-block;
    background: rgba(212, 175, 55, 0.1);
    color: var(--accent-gold);
    padding: 0.5rem 1rem;
    border-radius: 50px;
    font-size: 0.9rem;
    font-weight: 600;
    align-self: flex-start;
}

.product-sku {
    color: var(--text-gray);
    font-size: 1rem;
}

.product-sku span {
    color: var(--accent-teal);
    font-weight: 600;
}

.product-meta {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    padding: 1.5rem;
    background: rgba(212, 175, 55, 0.05);
    border-left: 3px solid var(--accent-gold);
    border-radius: 10px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    color: var(--text-gray);
}

.meta-item i {
    color: var(--accent-gold);
    font-size: 1.1rem;
}

.product-description h3,
.product-features h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    color: var(--accent-gold);
    margin-bottom: 1rem;
}

.product-description >>> p,
.product-features >>> p {
    color: var(--text-gray);
    line-height: 1.8;
    margin-bottom: 1rem;
}

.product-description >>> ul,
.product-features >>> ul {
    list-style: none;
    padding-left: 0;
}

.product-description >>> li,
.product-features >>> li {
    color: var(--text-gray);
    padding: 0.5rem 0;
    padding-left: 1.5rem;
    position: relative;
}

.product-description >>> li::before,
.product-features >>> li::before {
    content: '▸';
    position: absolute;
    left: 0;
    color: var(--accent-teal);
}

.product-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 2rem;
}

.btn-action {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
    padding: 1rem 2rem;
    border-radius: 50px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.btn-primary {
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    color: var(--primary-dark);
}

.btn-primary:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(212, 175, 55, 0.4);
}

.btn-secondary {
    background: rgba(45, 212, 191, 0.1);
    color: var(--accent-teal);
    border-color: var(--accent-teal);
}

.btn-secondary:hover {
    background: var(--accent-teal);
    color: var(--primary-dark);
}

.btn-contact {
    background: rgba(212, 175, 55, 0.1);
    color: var(--accent-gold);
    border-color: var(--accent-gold);
}

.btn-contact:hover {
    background: var(--accent-gold);
    color: var(--primary-dark);
}

/* RELATED PRODUCTS */
.related-products {
    padding: 80px 2rem;
    background: linear-gradient(180deg, transparent, rgba(30, 58, 95, 0.1));
}

.section-title {
    font-family: 'Playfair Display', serif;
    font-size: 2.5rem;
    font-weight: 700;
    text-align: center;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, var(--text-light), var(--accent-gold));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.section-subtitle {
    text-align: center;
    color: var(--text-gray);
    margin-bottom: 3rem;
    font-size: 1.1rem;
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
}

/* RESPONSIVE */
@media (max-width: 1024px) {
    .product-layout {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .page-title {
        font-size: 2rem;
    }
    
    .main-image {
        height: 400px;
    }
    
    .products-grid {
        grid-template-columns: 1fr;
    }
}
</style>